import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { Crosshair, FilePlus2, FolderOpen, X } from 'lucide-react'

import { type EntradaIfcx, ifcxApi } from '@viewer/ifcx/ifcxApi'
import { propriedadesDoNo, type ElementoIfcx } from '@viewer/ifcx/ifcxParse'

interface Props {
  aoFechar: () => void
}

/** IFC5 (`.ifcx`): abrir, ver e empilhar camadas.
 *
 *  O painel mostra o que o formato tem de diferente, que é a
 *  composição: abre-se um ficheiro, desenha-se, e depois acrescenta-se
 *  por cima um segundo ficheiro de meio kilobyte que muda uma
 *  propriedade sem tocar no primeiro. Ver isso a acontecer explica o
 *  IFC5 melhor do que qualquer texto.
 */
export default function IfcxPanel({ aoFechar }: Props) {
  const entradas = useSyncExternalStore(ifcxApi.subscribe, ifcxApi.lista)
  const [erro, setErro] = useState<string | null>(null)
  const [aCarregar, setACarregar] = useState(false)
  // Qual dos ficheiros abertos está em foco. Cai para o primeiro
  // quando o escolhido deixa de existir, e por isso não precisa de ser
  // corrigido por nenhum efeito: a queda faz-se na leitura.
  const [seleccionado, setSeleccionado] = useState<string | null>(null)
  const ficheiroRef = useRef<HTMLInputElement>(null)
  const camadaRef = useRef<HTMLInputElement>(null)

  const activa = entradas.find((e) => e.id === seleccionado) ?? entradas[0] ?? null

  const correr = async (fn: () => Promise<unknown>) => {
    setACarregar(true)
    setErro(null)
    try {
      await fn()
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui abrir esse ficheiro.')
    } finally {
      setACarregar(false)
    }
  }

  return (
    <div className="flex max-h-[70vh] w-[420px] flex-col text-sm">
      <div className="flex shrink-0 items-center gap-2 border-b border-[var(--color-border)] px-3 py-2">
        <span className="font-medium">IFC5 (.ifcx)</span>
        <span className="rounded border border-[var(--color-warn-border)] bg-[var(--color-warn-bg)] px-1 text-[9px] uppercase tracking-wider text-[var(--color-warn-text)]">
          alfa
        </span>
        <button
          type="button"
          onClick={aoFechar}
          aria-label="Fechar"
          className="ml-auto rounded p-0.5 text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-[var(--color-border)] px-3 py-2 text-[11px]">
        <button
          type="button"
          disabled={aCarregar}
          onClick={() => ficheiroRef.current?.click()}
          className="flex items-center gap-1 rounded border border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] px-2 py-1 font-medium text-[var(--color-text)] hover:bg-[var(--color-accent-hover)] disabled:opacity-50"
        >
          <FolderOpen size={12} aria-hidden />
          Abrir .ifcx
        </button>
        <input
          ref={ficheiroRef}
          type="file"
          accept=".ifcx,application/json"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0]
            e.target.value = ''
            if (f) void correr(() => ifcxApi.abrir(f))
          }}
        />
        {activa && (
          <>
            <button
              type="button"
              disabled={aCarregar}
              onClick={() => camadaRef.current?.click()}
              title="Acrescentar um ficheiro por cima deste. É o que o IFC5 faz de diferente: a camada muda o que quiser sem tocar no ficheiro base."
              className="flex items-center gap-1 rounded border border-[var(--color-border)] px-2 py-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-50"
            >
              <FilePlus2 size={12} aria-hidden />
              Acrescentar camada
            </button>
            <input
              ref={camadaRef}
              type="file"
              accept=".ifcx,application/json"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0]
                e.target.value = ''
                if (f) void correr(() => ifcxApi.acrescentarCamada(activa.id, f))
              }}
            />
            <button
              type="button"
              onClick={() => ifcxApi.enquadrar(activa.id)}
              className="flex items-center gap-1 rounded border border-[var(--color-border)] px-2 py-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
            >
              <Crosshair size={12} aria-hidden />
              Enquadrar
            </button>
            <button
              type="button"
              onClick={() => ifcxApi.fechar(activa.id)}
              className="ml-auto rounded border border-[var(--color-border)] px-2 py-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
            >
              Fechar
            </button>
          </>
        )}
      </div>

      {entradas.length > 1 && (
        <div className="flex shrink-0 flex-wrap gap-1 border-b border-[var(--color-border)] px-3 py-1.5 text-[10px]">
          {entradas.map((e) => (
            <button
              key={e.id}
              type="button"
              onClick={() => setSeleccionado(e.id)}
              className={`max-w-[160px] truncate rounded px-1.5 py-0.5 ${
                e.id === activa?.id
                  ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
              }`}
              title={e.ficheiro}
            >
              {e.ficheiro}
            </button>
          ))}
        </div>
      )}

      {erro && (
        <p className="shrink-0 border-b border-[var(--color-error-border)] bg-[var(--color-error-bg)] px-3 py-2 text-[11px] text-[var(--color-error-text)]">
          {erro}
        </p>
      )}

      {!activa ? (
        <div className="px-3 py-4 text-[11px] text-[var(--color-text-muted)]">
          <p>
            O IFC5 é a próxima geração do IFC, e ainda está em alfa. Em vez
            de entidades com atributos fixos, tem nós sobre os quais se
            empilham pedaços de informação, que podem vir em ficheiros
            separados.
          </p>
          <p className="mt-2">
            Abre um <code>.ifcx</code> para o ver desenhado. Depois
            acrescenta-lhe uma camada e repara no que muda.
          </p>
          <p className="mt-2 text-[var(--color-text-dim)]">
            Os exemplos oficiais estão em github.com/buildingSMART/IFC5-development.
          </p>
        </div>
      ) : (
        <Conteudo entrada={activa} />
      )}
    </div>
  )
}

function Conteudo({ entrada }: { entrada: EntradaIfcx }) {
  const [filtro, setFiltro] = useState('')
  const { elementos, desconhecidas, fronteiras } = entrada.cena
  const seleccao = useSyncExternalStore(ifcxApi.subscribe, ifcxApi.seleccionado)

  /** Que espaços é que cada elemento limita, e que elementos limitam
   *  cada espaço. Lê-se nos dois sentidos porque interessa nos dois: de
   *  uma parede quer-se saber que sala fecha, e de uma sala quer-se
   *  saber o que a fecha. */
  const vizinhos = useMemo(() => {
    const mapa = new Map<string, string[]>()
    const juntar = (a: string, b: string) => {
      const l = mapa.get(a)
      if (l) l.push(b)
      else mapa.set(a, [b])
    }
    for (const f of fronteiras) {
      juntar(f.elemento, f.espaco)
      juntar(f.espaco, f.elemento)
    }
    return mapa
  }, [fronteiras])

  const nomes = useMemo(() => {
    const m = new Map<string, string>()
    for (const e of elementos) m.set(e.path, e.nome)
    return m
  }, [elementos])

  const triangulos = useMemo(
    () =>
      elementos.reduce(
        (s, e) => s + e.malhas.reduce((t, m) => t + m.indices.length / 3, 0),
        0,
      ),
    [elementos],
  )

  const visiveis = useMemo(() => {
    const q = filtro.trim().toLowerCase()
    const comInteresse = elementos.filter((e) => e.classe || e.malhas.length > 0)
    if (!q) return comInteresse
    return comInteresse.filter(
      (e) =>
        e.nome.toLowerCase().includes(q) ||
        (e.classe ?? '').toLowerCase().includes(q) ||
        e.caminho.toLowerCase().includes(q),
    )
  }, [elementos, filtro])

  return (
    <>
      <div className="shrink-0 border-b border-[var(--color-border)] px-3 py-2 text-[10px] text-[var(--color-text-muted)]">
        <p className="truncate font-medium text-[var(--color-text)]">
          {entrada.ficheiro}
        </p>
        <p className="mt-0.5">
          {entrada.cena.nos} nós compostos · {elementos.length} na árvore ·{' '}
          {triangulos} triângulos
          {fronteiras.length > 0 && <> · {fronteiras.length} fronteiras</>}
        </p>
        {entrada.camadas.length > 0 && (
          <p className="mt-0.5 text-[var(--color-accent)]">
            Camadas por cima: {entrada.camadas.join(', ')}
          </p>
        )}
        {desconhecidas.length > 0 && (
          <p
            className="mt-0.5 text-[var(--color-text-dim)]"
            title="Chaves que este ficheiro traz e que ainda não sabemos ler. Ficam à vista em vez de desaparecerem: num formato alfa, isto é a lista de trabalho."
          >
            Por ler: {desconhecidas.join(', ')}
          </p>
        )}
      </div>

      <div className="shrink-0 px-3 py-1.5">
        <input
          type="text"
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
          placeholder="Filtrar por nome, classe ou caminho"
          className="w-full rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1 text-[11px] text-[var(--color-text)] placeholder:text-[var(--color-text-dim)]"
        />
      </div>

      <ul className="min-h-0 flex-1 overflow-y-auto px-3 pb-3 text-[11px]">
        {visiveis.map((e, i) => (
          // O mesmo nó pode aparecer duas vezes na árvore, e isso não é
          // um erro: um protótipo herdado por dois sítios dá duas
          // colocações do mesmo `path`. O React precisa na mesma de uma
          // chave única, senão junta as duas linhas numa e a segunda
          // desaparece.
          <Elemento
            key={`${e.path}-${i}`}
            elemento={e}
            escolhido={seleccao?.path === e.path}
            limita={(vizinhos.get(e.path) ?? []).map(
              (p) => nomes.get(p) ?? p.slice(0, 8),
            )}
          />
        ))}
        {visiveis.length === 0 && (
          <li className="py-2 italic text-[var(--color-text-muted)]">
            Nada corresponde a esse filtro.
          </li>
        )}
      </ul>
    </>
  )
}

/** As propriedades que vale a pena mostrar de um nó: as do IFC e as do
 *  material, incluindo as qualificadas pelo property set. O resto é
 *  geometria e presentação, que já se vê no 3D. */
function propriedades(e: ElementoIfcx) {
  return propriedadesDoNo(e.atributos)
}

function comoTexto(v: unknown): string {
  if (typeof v === 'string') return v
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  if (v && typeof v === 'object') {
    const c = (v as { code?: unknown }).code
    if (typeof c === 'string') return c
  }
  return JSON.stringify(v) ?? ''
}

function Elemento({
  elemento,
  escolhido,
  limita,
}: {
  elemento: ElementoIfcx
  escolhido: boolean
  limita: string[]
}) {
  const { propriedades: props, colisoes } = propriedades(elemento)
  const ref = useRef<HTMLLIElement>(null)

  // Clicar na parede no 3D tem de trazer a linha dela à vista, senão a
  // selecção só se vê no modelo e a lista fica a mentir.
  useEffect(() => {
    if (escolhido) ref.current?.scrollIntoView({ block: 'nearest' })
  }, [escolhido])

  return (
    <li
      ref={ref}
      className={`border-b border-[var(--color-border)] py-1.5 last:border-0 ${
        escolhido ? 'bg-[var(--color-accent-bg)]' : ''
      }`}
    >
      <div className="flex items-baseline gap-2">
        <span className="font-medium text-[var(--color-text)]">
          {elemento.nome}
        </span>
        {elemento.classe && (
          <span className="font-mono text-[10px] text-[var(--color-accent)]">
            {elemento.classe}
          </span>
        )}
        {!elemento.visivel && (
          <span className="text-[10px] text-[var(--color-text-dim)]">
            invisível
          </span>
        )}
      </div>
      <p className="truncate text-[10px] text-[var(--color-text-dim)]">
        {elemento.caminho || elemento.path}
      </p>
      {limita.length > 0 && (
        <p className="mt-0.5 text-[10px] text-[var(--color-text-dim)]">
          Fronteira com: {limita.join(', ')}
        </p>
      )}
      {props.length > 0 && (
        <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-[10px]">
          {props.map((p) => (
            <div key={p.chave} className="contents">
              <dt className="text-[var(--color-text-dim)]" title={p.chave}>
                {p.pset && (
                  <span className="text-[var(--color-text-muted)]">{p.pset} · </span>
                )}
                {p.nome}
              </dt>
              <dd className="break-words text-[var(--color-text-muted)]">
                {comoTexto(p.valor)}
              </dd>
            </div>
          ))}
        </dl>
      )}
      {colisoes.length > 0 && (
        // O mesmo nome vindo de sítios diferentes. Mostram-se os dois:
        // ficar com um deles em silêncio era decidir pelo ficheiro.
        <p className="mt-1 text-[10px] text-[var(--color-warn-text)]">
          Nome repetido em property sets diferentes:{' '}
          {colisoes
            .map((c) => `${c.nome} (${c.origens.map((o) => o.pset ?? 'sem pset').join(', ')})`)
            .join('; ')}
          . Mostram-se todos.
        </p>
      )}
    </li>
  )
}
